// Composer Studio — 편집 히스토리 훅 (Phase 3b-1 → 3b-2 transient → 선택 메타 동반)
//
// ReportTemplate 편집의 undo/redo 스택. {past, present, future} 모델.
// 뮤테이션은 함수형 producer((cur)=>next)를 받아 present에 적용하고 past에 push, future를 비운다.
// producer가 동일 참조(무변경)를 반환하면 히스토리에 push하지 않는다(불필요한 undo 단계 방지).
//
// [3b-2] transient 편집(리사이즈 드래그 등):
//   - replacePresent: present만 갱신(past/future 무변경) → 드래그 중 연속 미리보기에 사용
//   - commitPresent: "transient 시작 전 present"를 past에 push하고 producer 결과를 present로 →
//     연속 미리보기 N회를 undo 1회로 묶는다(pointerup에서 호출).
//   transient 시작 시점은 첫 replacePresent 호출에서 자동 스냅샷한다(commit/일반 apply 시 리셋).
//
// [선택 메타] 히스토리 엔트리에 세션 메타(선택 상태 등)를 스냅샷과 짝으로 동반한다(2026-07-17).
//   - 템플릿 데이터 자체는 불변(저장 JSON 오염 없음) — 훅은 M을 불투명 취급하는 주입식.
//   - 캡처는 commit 경계에서만: apply/commitPresent가 past에 push할 때 + undo/redo가 반대
//     스택에 push할 때 captureMeta(). replacePresent(transient 미리보기)는 캡처하지 않고,
//     discardTransient는 메타에 무영향(push 자체가 없음).
//   - undo/redo가 스냅샷을 present로 복귀시킬 때 restoreMeta(meta, 복원된 템플릿) 호출 —
//     둘째 인자는 복원 대상 id의 존재 확인(방어) 용도.

import { useCallback, useRef, useState } from 'react';
import type { ReportTemplate } from '@safetyhub/report-composer';

interface HistoryEntry<M> {
  template: ReportTemplate;
  /** 스냅샷과 짝으로 캡처된 세션 메타 — captureMeta 미설정(또는 undefined 반환) 시 undefined. */
  meta: M | undefined;
}

interface HistoryState<M> {
  past: HistoryEntry<M>[];
  present: ReportTemplate;
  future: HistoryEntry<M>[];
}

export interface EditorHistoryOptions<M> {
  /** commit 경계에서 호출 — 현재 present와 짝이 되는 세션 메타(선택 상태 등)를 반환. */
  captureMeta?: () => M;
  /** undo/redo로 스냅샷이 present로 복귀할 때 호출. restored = 복원된 템플릿(id 존재 확인용). */
  restoreMeta?: (meta: M, restored: ReportTemplate) => void;
}

export interface EditorHistory {
  present: ReportTemplate;
  /** producer로 present를 변환해 적용(무변경이면 push 생략) */
  apply: (producer: (cur: ReportTemplate) => ReportTemplate) => void;
  /** present만 갱신(히스토리 미기록) — transient 드래그 미리보기용. 첫 호출 시 시작 스냅샷 캡처. */
  replacePresent: (producer: (cur: ReportTemplate) => ReportTemplate) => void;
  /** transient 종료 — 시작 스냅샷을 past에 push + producer 결과를 present로(undo 1회로 묶음) */
  commitPresent: (producer: (cur: ReportTemplate) => ReportTemplate) => void;
  /** transient 폐기 — present를 시작 스냅샷(원본)으로 복원(past/future 무변경). 취소(Esc)용. */
  discardTransient: () => void;
  /** 히스토리 전체 초기화 — 새 템플릿 load/신규 시작점으로 교체(past/future 비움). */
  reset: (next: ReportTemplate) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useEditorHistory<M = unknown>(
  initial: ReportTemplate,
  options?: EditorHistoryOptions<M>,
): EditorHistory {
  const [state, setState] = useState<HistoryState<M>>({
    past: [],
    present: initial,
    future: [],
  });

  // 최신 상태 미러 — undo/redo가 pop한 entry의 meta로 restoreMeta를 setState updater 밖
  // (이벤트 시점)에서 동기 호출하기 위함(함수형 updater는 렌더 시점 실행이라 부수효과 금지).
  // 모든 뮤테이션이 이벤트 핸들러에서 update()를 거치므로 ref는 항상 state와 일치한다.
  // (blur 커밋→undo처럼 한 이벤트에서 뮤테이션이 연쇄해도 ref가 동기 갱신되어 순서대로 합성.)
  const stateRef = useRef<HistoryState<M>>(state);
  // captureMeta/restoreMeta는 매 렌더 최신 클로저 유지(호출부가 인라인 함수를 넘겨도 안전).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const update = useCallback((next: HistoryState<M>) => {
    stateRef.current = next;
    setState(next);
  }, []);

  // transient 시작 스냅샷(첫 replacePresent에서 present를 기록, commit/일반 apply에서 리셋).
  const transientStart = useRef<ReportTemplate | null>(null);

  const apply = useCallback(
    (producer: (cur: ReportTemplate) => ReportTemplate) => {
      transientStart.current = null; // 일반 apply는 진행 중 transient를 무효화
      const s = stateRef.current;
      const next = producer(s.present);
      if (next === s.present) return; // 무변경 — 히스토리 미기록
      update({
        past: [...s.past, { template: s.present, meta: optionsRef.current?.captureMeta?.() }],
        present: next,
        future: [],
      });
    },
    [update],
  );

  const replacePresent = useCallback(
    (producer: (cur: ReportTemplate) => ReportTemplate) => {
      const s = stateRef.current;
      // transient 시작 스냅샷이 없으면 현재 present를 시작점으로 캡처(commit 시 undo 대상).
      if (transientStart.current === null) transientStart.current = s.present;
      const next = producer(s.present);
      if (next === s.present) return;
      update({ ...s, present: next }); // past/future 무변경 (메타 캡처도 없음 — commit 경계 아님)
    },
    [update],
  );

  const commitPresent = useCallback(
    (producer: (cur: ReportTemplate) => ReportTemplate) => {
      const s = stateRef.current;
      const start = transientStart.current ?? s.present;
      transientStart.current = null;
      const next = producer(s.present);
      // 시작점과 최종이 같으면 히스토리 미기록.
      if (next === start) {
        if (next !== s.present) update({ ...s, present: next });
        return;
      }
      // 메타는 commit 시점에 캡처 — transient(드래그) 동안 선택은 바뀌지 않는 것이 전제.
      update({
        past: [...s.past, { template: start, meta: optionsRef.current?.captureMeta?.() }],
        present: next,
        future: [],
      });
    },
    [update],
  );

  const discardTransient = useCallback(() => {
    const s = stateRef.current;
    const start = transientStart.current;
    transientStart.current = null;
    if (start === null || start === s.present) return;
    update({ ...s, present: start }); // 시작점(원본)으로 present 복원
  }, [update]);

  const reset = useCallback(
    (next: ReportTemplate) => {
      transientStart.current = null;
      // past/future 통째 폐기 — 이전 템플릿의 선택 메타도 함께 사라져 잔존 없음.
      update({ past: [], present: next, future: [] });
    },
    [update],
  );

  const undo = useCallback(() => {
    transientStart.current = null;
    const s = stateRef.current;
    if (s.past.length === 0) return;
    const entry = s.past[s.past.length - 1];
    update({
      past: s.past.slice(0, -1),
      present: entry.template,
      // redo 대칭: 현 present도 현시점 메타와 짝지어 future에 push.
      future: [{ template: s.present, meta: optionsRef.current?.captureMeta?.() }, ...s.future],
    });
    if (entry.meta !== undefined) optionsRef.current?.restoreMeta?.(entry.meta, entry.template);
  }, [update]);

  const redo = useCallback(() => {
    transientStart.current = null;
    const s = stateRef.current;
    if (s.future.length === 0) return;
    const entry = s.future[0];
    update({
      past: [...s.past, { template: s.present, meta: optionsRef.current?.captureMeta?.() }],
      present: entry.template,
      future: s.future.slice(1),
    });
    if (entry.meta !== undefined) optionsRef.current?.restoreMeta?.(entry.meta, entry.template);
  }, [update]);

  return {
    present: state.present,
    apply,
    replacePresent,
    commitPresent,
    discardTransient,
    reset,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
